import { Models as FlightModels } from '@luxuryescapes/lib-flights-types'
import type { definitions as flightDefinitions, paths } from '@luxuryescapes/contract-svc-flights'
import { templates } from '@luxuryescapes/lib-uri-templates'
import request, { authOptions } from 'api/requestUtils'
import {
  Airline,
  FlightCachedPrice,
  FlightCalendarPrices,
  FlightsCreditDetails,
} from 'components/Flights/types'
import config from 'constants/config'
import { FlightsFareTypes } from 'constants/flight'
import { nonNullable, sortBy } from 'lib/array/arrayUtils'
import { EmptyObject } from 'lib/object/objectUtils'
import { countOccupantsForFlights } from 'lib/offer/occupancyUtils'
import qs from 'qs'
import { flightDealMap } from './lib/flightDealMap'
import { mapFlightFareFamily, ServerUpsellFareFamily } from './lib/flightFareFamilyMap'
import { journeyV2Map } from './lib/journeyV2Map'
import { seatsAvailabilityMap } from './lib/seatsAvailabilityMap'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import { resetFlightsTimer } from 'components/Flights/FlightsSessionTimer/FlightsSessionTimer'
export interface FlightSearchItinerary {
  departureAirport: string
  arrivalAirport: string
  departureDate: string
}

/** We want to stop using the types from lib-flight-types and use our own types
 * defined on CP even for the response from the server.
 * This is a temporary solution until we migrate to the new types.
 */
type Carrier = {
  code: string
  name: string
  logoUrl: string
  brandMarkUrl?: string
}

type Flight = FlightModels.Search.Flight & { marketingAirline: Carrier; seatsLeft?: number }

type FlightGroup = FlightModels.Search.FlightGroup & { flights: Array<Flight> }

export type Fare = FlightModels.Search.Fare & { flightGroups: Array<FlightGroup>, exchangeType?: 'ORIGINAL' | 'NEW', paymentScheduleSupported?: boolean }

type OneWayFare = FlightModels.Search.OneWayFare & { fares: Array<Fare> }

export type ServerSeatResponse = flightDefinitions['SeatsAvailabilityResponse']

interface SearchParams {
  itinerary: Array<FlightSearchItinerary>
  currency: string
  occupancies: Array<App.Occupants>
  fareClass: string
  maxArrivalTime?: string
  minReturningDepartureTime?: string
  region: string
  carriers?: Array<string>
  fareType?: string
  source?: string
  viewType?: string
  carrier?: string
  flightsCredit?: boolean
  forceBundleId?: string
  filterAirlines?: Array<string>
  dealId?: string
  charterSearchId?: string
}

interface FlightSearchResult {
  viewType?: string
  searchId?: string
  onewayFares?: Array<OneWayFare>
  journeys?: Array<any>
  notices?: Array<App.FlightNotice>
  sort?: string
  fareFamilies?: FlightModels.Search.IFareFamilies
  metadata?: {
    cheapest?: {
      adultPrice?: number
      provider?: string
    }
  }
}

export interface ServerFlightDateChangeUpsellFaresResult {
  upsellPerLeg: Array<{ legId: string, upsellFares: Array<ServerUpsellFareFamily> }>
  isMixedUpsell?: boolean
}

interface ServerExchangeDateFlightSearchResult {
  onewayFares: Array<OneWayFare>
  fareFamilies: FlightModels.Search.IFareFamilies
  searchId: string
  fareIdsToUpsellFarePerLeg: Record<string, ServerFlightDateChangeUpsellFaresResult>
}

export interface MappedFlightSearchResult {
  onewayFares: Array<Array<App.JourneyV2>>
  searchId?: string
  sessionId?: string
  viewType?: string
  metadata?: {
    cheapest?: {
      adultPrice?: number
      provider?: string
    }
  }
  timestamp?: number
  journeys?: Array<App.Journey>
  notices?: Array<App.FlightNotice>
  sort?: string
}

const fareTypeToTripTypeMapper: Record<FlightsFareTypes, string> = {
  [FlightsFareTypes.RETURN]: 'Return',
  [FlightsFareTypes.ONE_WAY]: 'OneWay',
  [FlightsFareTypes.MULTI_CITY]: 'MultiCity',
}

export const tripTypeToFareTypeMapper: Record<string, FlightsFareTypes> = {
  Return: FlightsFareTypes.RETURN,
  OneWay: FlightsFareTypes.ONE_WAY,
  MultiCity: FlightsFareTypes.MULTI_CITY,
  TwoOneWays: FlightsFareTypes.RETURN,
  TwoOneWaysAndReturn: FlightsFareTypes.RETURN,
}

export function searchFlights(
  {
    itinerary,
    currency,
    occupancies,
    fareClass,
    maxArrivalTime,
    minReturningDepartureTime,
    region,
    fareType,
    source,
    viewType,
    carrier,
    flightsCredit,
    forceBundleId,
    filterAirlines,
    dealId,
    charterSearchId,
  }: SearchParams,
  options: {
    offerId?: string
    publicOfferFlightPrice?: number
    offerCalendarPrice?: number
    enableSabreContent?: false
  } = EmptyObject,
): Promise<MappedFlightSearchResult> {
  const passengerCount = countOccupantsForFlights(occupancies)

  const payload: any = {
    tripType: fareTypeToTripTypeMapper[fareType as FlightsFareTypes] || fareTypeToTripTypeMapper[FlightsFareTypes.RETURN],
    currencyCode: currency,
    itinerary,
    fareClass,
    passengers: {
      numAdults: passengerCount.adults,
      numChildren: passengerCount.children,
      numInfants: passengerCount.infants,
    },
    region,
    source,
    viewType,
    carrier,
    flightsCredit,
    sessionId: '',
    searchId: '',
    filters: {
      ...(filterAirlines ? { airlines: filterAirlines } : {}),
    },
    sortBy: '',
    searchDateTime: '',
    searchSource: '',
    localeId: '',
    apiAuthKey: '',
    forceBundleId,
    dealId,
    charterSearchId,
    ...options,
  }

  const providerOverrides = config.FLIGHT_PROVIDER_OVERRIDE
  if (providerOverrides) {
    payload.provider = providerOverrides.split(',')
  }

  if (maxArrivalTime) {
    payload.maxArrivalTime = maxArrivalTime
  }

  if (minReturningDepartureTime) {
    payload.minReturningDepartureTime = minReturningDepartureTime
  }

  const headers: Record<string, string> = { 'x-experiment-id': options?.enableSabreContent ? OptimizelyFeatureFlags.flightsEnableSabreContent : '' }

  return request
    .post<App.ApiResponse<FlightSearchResult>, any>('/api/flights/flightsearch', payload, { credentials: 'include', headers })
    .then<MappedFlightSearchResult>(res => {
      const { onewayFares, searchId, viewType, sort, fareFamilies, metadata } = res.result

      resetFlightsTimer()

      return {
        onewayFares: (onewayFares ?? []).map((oneWayFare) => {
          return oneWayFare.fares.filter(fare => fare !== null).map((fare) => journeyV2Map(fare, fareFamilies))
        }),
        searchId,
        viewType,
        sort: sort?.toLowerCase(),
        fareFamilies,
        metadata,
        timestamp: Date.now(),
      }
    })
}

export interface FareRuleParams {
  provider: string
  fare_rule_type?: string
  pnrId?: string
  region?: string
  journey_id?: string
  isBundledWithAccommodation?: boolean
}

export function getFareRules(url: string, fareRuleParams?: FareRuleParams) {
  if (fareRuleParams) {
    return request.post<App.ApiResponse<any>, unknown>(url, fareRuleParams).then(resp => resp.result)
  }
  return request.get<App.ApiResponse<any>>(url).then(resp => resp.result)
}

export type SelectedSeatPayload = {
  fareId: string
  flightNumber: string
  marketingAirlineCode: string
  operatingAirlineCode: string
  passengerId: string
  seatNumber: string
}

export type AdditionalSelectFlightsPayload = {
  seatSelections?: Array<SelectedSeatPayload>
}

export interface FlightSelectParams {
  journeyId: string
  departingJourneyKey: string
  departingFareFamilyId?: string
  returningJourneyKey?: string
  returningFareFamilyId?: string
  departingAirport: string
  arrivalAirport: string
  providerSearchId: string
  region?: string
  isStandalone?: boolean
  provider: string
  source?: string
  callCount?: number
  isFlightsCredit?: boolean
  viewType?: string
  selectedFareIds?: Array<string>
  isFlightBaggageAvailable?: boolean
  forceBundleId?: string
  selectedUpsellOptions?: Record<string, string>
  additionalPayload?: AdditionalSelectFlightsPayload
}

export interface PriceDetails {
  passengerTypeQuantity: { code: string }
  totalFare: string
  totalFareOriginalCurrency: number
}

export interface Price {
  type: 'total_fare' | 'atol_fee' | 'service_fee' | 'flights_credit_fee' | 'booking_fee'
  amount: number
}

type SelectFlightsResponse = paths['/api/flights/select']['post']['responses'][200]['content']['application/json']
type SelectFlightsResult = Extract<SelectFlightsResponse, { status: 200 }>['result'] & {
  selectedSeatsInformation?: { totalCost: number }
}

export function selectflights({
  journeyId,
  departingJourneyKey,
  departingFareFamilyId,
  returningJourneyKey,
  returningFareFamilyId,
  departingAirport,
  arrivalAirport,
  providerSearchId,
  region,
  isStandalone,
  provider,
  source,
  isFlightsCredit,
  viewType,
  selectedFareIds,
  forceBundleId,
  selectedUpsellOptions,
  additionalPayload,
}: FlightSelectParams): Promise<App.ApiResponse<SelectFlightsResult>> {
  const selectFlightDetails: any = {
    journeyId,
    departing_journey_key: departingJourneyKey,
    returning_journey_key: returningJourneyKey,
    departing_airport: departingAirport,
    arrival_airport: arrivalAirport,
    provider_search_id: providerSearchId,
    region,
    isStandalone,
    provider,
    source,
    isFlightsCredit,
    viewType,
    selectedFareIds,
    forceBundleId,
  }

  if (selectedUpsellOptions && Object.keys(selectedUpsellOptions).length > 0) {
    selectFlightDetails.selectedUpsellOptions = selectedUpsellOptions
  } else if (selectedFareIds && (departingFareFamilyId || returningFareFamilyId)) {
    // Use default mapping if selectedUpsellOptions is not provided
    selectFlightDetails.selectedUpsellOptions = {
      [selectedFareIds[0]]: departingFareFamilyId,
      [selectedFareIds[1]]: returningFareFamilyId,
    }
  }

  if (additionalPayload?.seatSelections) {
    selectFlightDetails.selectedSeats = additionalPayload.seatSelections
  }

  // disable booking fee for any flights in AU region, apply merchant fee for merchant fee evaluation
  return request
    .post<App.ApiResponse<SelectFlightsResult>, any>('/api/flights/select', selectFlightDetails).then(response => {
      if (response.result.error?.message) {
        throw new Error(response.result.error.message)
      }
      return response
    })
}

interface ServerAirportLocation {
  id?: number
  cityAirportName?: string
  cityCode?: string
  cityDesc?: string
  airportCode: string
  airportName: string
  country?: string
  isMultiAirport?: boolean
  isChildDisplay?: boolean
  isCityAirport?: boolean
  lt?: number
  lg?: number
}

function airportMap(airport: any): App.AirportLocation {
  return {
    id: airport.id,
    cityAirportName: airport.cityAirportName,
    cityCode: airport.cityCode,
    cityDesc: airport.cityDesc,
    airportCode: airport.airportCode,
    airportName: airport.airportName,
    country: airport.country,
    isMultiAirport: airport.isMultiAirport,
    isChildDisplay: airport.isChildDisplay,
    lt: airport.lt,
    lg: airport.lg,
  }
}

export function getAirportsSearch(searchText: string): Promise<Array<App.AirportLocation>> {
  const query = qs.stringify({
    searchCode: searchText,
  })
  return request
    .get<Array<ServerAirportLocation>>(`/api/flights/locationSearch?${query}`)
    .then(res => res.map(airport => airportMap(airport)))
}

interface AirportResult {
  airports?: Record<string, App.Airport>
  origins?: Array<string>
  destinations?: Array<string>
  main_port?: string
  closest_port?: string
}

export function getAirports(regionCode: string): Promise<{
  airports: Array<App.Airport>
  originAirports: Array<App.Airport>
  mainAirport?: App.Airport
}> {
  const query = qs.stringify({ region: regionCode })

  return request.get<App.ApiResponse<AirportResult>>(`/api/flights/airports?${query}`).then(res => {
    const originAirports = nonNullable((res.result.origins ?? []).map(code => res.result.airports?.[code]))
    return {
      airports: Object.values(res.result.airports ?? {}),
      originAirports: sortBy(originAirports, airport => airport.name, 'asc'),
      mainAirport: res.result.airports?.[res.result.main_port ?? ''],
    }
  })
}

export async function getFlightPrice({
  startDate,
  endDate,
  numberOfAdults,
  numberOfChildren,
  numberOfInfants,
  origin,
  destination,
  currency,
  region,
  nights,
}: {
  startDate: string
  endDate: string
  numberOfAdults: number
  numberOfChildren: number
  numberOfInfants: number
  origin?: string
  destination: string
  currency: string
  region: string
  nights?: number
}): Promise<number> {
  const uri = templates.flight.flight_single_cheapest.expand({
    start_date: startDate,
    end_date: endDate,
    origin,
    currency,
    number_of_nights: nights,
    number_of_adults: numberOfAdults,
    number_of_children: numberOfChildren,
    number_of_infants: numberOfInfants,
    region,
    destination,
  })

  const response = await request.get<App.ApiResponse<any>>(uri)

  if (response?.result?.journey) {
    return response.result.journey.cost + response.result.journey.fees
  }
  return 0
}

export function getPopularAirportsByRegion(region: string) {
  return request
    .get<App.ApiResponse<App.PopularAirports>>(`/api/flights/popularDestinations?region=${region}`)
    .then(res => {
      const { origins, destinations } = res.result

      return {
        origins: origins.map(airport => airportMap(airport)),
        destinations: destinations.map(airport => airportMap(airport)),
      }
    })
}

export function getCreditRebookingDetails(email: string, pnrId: string) {
  return request
    .post<App.ApiResponse<any>, Record<string, string>>('/api/flights/postBookingPnrDetails', { email, pnrId }, { credentials: 'include' })
    .then(res => {
      const { status, message, result } = res

      const creditDetails: FlightsCreditDetails = {
        passengerCounts: result.passengerCounts,
        flightCreditFee: result.flightCreditFee,
        currency: result.currency,
        carrier: result.carrier?.validating,
        carrierName: result.carrier?.carrierName,
        pnrId,
        region: result.region,
        email: result.email,
        phone: result.phone,
      }

      return {
        status,
        message,
        creditDetails,
      }
    })
}

export function getFlightDealsByRegion(region: string, dealCategory?: string): Promise<Array<App.FlightDeal>> {
  return request
    .post<App.ApiResponse<Array<App.FlightDeal>>, Record<string, string | undefined>>('/api/flights/flight-deals/getFilteredDeals', { region, categoryGroup: dealCategory })
    .then(res => res.result.map(flightDeal => flightDealMap(flightDeal)))
}

export function getFlightDeal(id: string, token?: string): Promise<App.FlightDeal> {
  return request.get<App.ApiResponse<App.FlightDeal>>(`/api/flights/flight-deals/${id}`, {
    credentials: 'include',
    ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
  }).then(response => flightDealMap(response.result))
}

interface GetFlightCampaignsResult {
  rows: Array<App.FlightCampaign>
  count: number
  isEnabled: boolean
}

export function getFlightCampaigns(region: string): Promise<GetFlightCampaignsResult> {
  return request.get<App.ApiResponse<GetFlightCampaignsResult>>(`/api/flights/campaigns?region=${region}&active=true`)
    .then(res => res.result)
}

export function getFlightCampaign(id: string): Promise<App.FlightCampaign> {
  return request.get<App.ApiResponse<App.FlightCampaign>>(`/api/flights/campaigns/${id}`).then(res => res.result)
}

export function getFlightCampaignDeals(id: string): Promise<Array<App.FlightDeal>> {
  return request.get<App.ApiResponse<{ rows: Array<App.FlightDeal>, count: number }>>(`/api/flights/campaigns/${id}/deals`).then(res => res.result.rows.map(flightDealMap))
}

export function getAirlines(searchText: string): Promise<Array<App.Airline>> {
  return request
    .get<Array<Airline>>(`/api/flights/airlines?searchCode=${searchText}`)
    .then(res => res.map(airline => airline))
}

export async function getJourneyV2(
  searchId: string,
  fareId: string,
  accessToken: string,
  fareFamilyId?: string,
): Promise<App.JourneyV2> {
  const query = qs.stringify({
    selectedUpsellOptionId: fareFamilyId,
  })
  return request.get<App.ApiResponse<Fare>>(
    `/api/flights/flightSearch/${searchId}/${fareId}?${query}`,
    authOptions(accessToken),
  ).then(response => {
    // server doesn't respond with correct status but hides the status in a 200 response, we need to detect this
    if (response.errors?.message) {
      throw new Error(response.errors.message)
    }
    return journeyV2Map(response.result)
  })
}

export async function getDateChangeJourneyV2(
  reservationId: string,
  searchId: string,
  fareId: string,
  selectedUpsellOptionId?: string,
): Promise<App.JourneyV2> {
  const queryParams = selectedUpsellOptionId ? `?selectedUpsellOptionId=${selectedUpsellOptionId}` : ''
  return request.get<App.ApiResponse<Fare>>(`/api/flights/reservation/${reservationId}/addons/exchange-dates/search/${searchId}/${fareId}${queryParams}`, { credentials: 'include' })
    .then(response => {
      if (response.errors?.message) {
        throw new Error(response.errors.message)
      }
      return journeyV2Map(response.result)
    })
}

interface CachePricingRequest {
  startDate: string
  endDate: string
  origin: string
  destination: string
  currency: string
  region: string
  nights?: number
}

interface CachePricingResult {
  journeys_by_date: FlightCachedPrice
  earliest_booking_date: string
  final_booking_date: string
}

export function getFlightCachePrices(params: CachePricingRequest): Promise<FlightCalendarPrices> {
  const { startDate, endDate, origin, destination, currency, region, nights } = params
  const queryParams = {
    start_date: startDate,
    end_date: endDate,
    number_of_adults: 2,
    number_of_children: 0,
    number_of_infants: 0,
    number_of_nights: nights || 1,
    destination,
    origin,
    currency,
    region,
  }

  return request
    .get<App.ApiResponse<CachePricingResult>>(`/api/flights/calendar-search?${qs.stringify(queryParams)}`)
    .then(res => ({ cachedPrices: res.result.journeys_by_date, earliestBookingDate: res.result.earliest_booking_date, finalBookingDate: res.result.final_booking_date }))
}

export function getFlightBookingFees(region: string): Promise<Array<string>> {
  return request
    .post<App.ApiResponse<Array<string>>, Record<string, string>>('/api/flights/bookingFees', { region })
    .then(resp => resp.result)
}

interface GetFlightUpsellFaresParams {
  searchId: string
  sessionId: string
  origin: string
  destination: string
  occupancies: App.Occupants
  region: string
  departingUpsellOptionId?: string
  isStandalone?: boolean
  selectedFareIds: Array<string>
}

export interface UpsellFaresResult {
  upsellFares: Array<ServerUpsellFareFamily>
  upsellPerLeg?: Array<{ legId: string, upsellFares: Array<ServerUpsellFareFamily> }>
  isMixedUpsell?: boolean
}

export type FareFamiliesResponse = {
  upsellFares: Array<App.FlightFareFamily>
  upsellFaresPerLeg?: Array<Array<App.FlightFareFamily>>
  isMixedUpsell?: boolean
}

export function getFlightFareFamilies({
  sessionId,
  searchId,
  origin,
  destination,
  occupancies,
  region,
  departingUpsellOptionId,
  isStandalone,
  selectedFareIds,
}: GetFlightUpsellFaresParams): Promise<FareFamiliesResponse> {
  const queryParams = {
    origin,
    destination,
    numAdults: occupancies.adults,
    numChildren: occupancies.children,
    numInfants: occupancies.infants,
    region,
    sessionId,
    searchId,
    departingUpsellOptionId,
    isStandalone,
    selectedFareIds: selectedFareIds?.join(','),
  }

  return request
    .get<App.ApiResponse<UpsellFaresResult>>(`/api/flights/fare-families/upsell?${qs.stringify(queryParams)}`)
    .then(res => {
      const upsellFares = res.result.upsellFares.map(mapFlightFareFamily)
      const upsellFaresPerLeg = res.result.upsellPerLeg?.map(leg => leg.upsellFares.map(mapFlightFareFamily))

      return { upsellFares, upsellFaresPerLeg, isMixedUpsell: res.result.isMixedUpsell }
    })
}

interface UpdateFlightTravellers {
  passengers: Array<App.FlightTraveller>
}

export function updateFlightReservationTravellers(reservationId: string, passengers: Array<App.FlightTraveller>) {
  const url = `/api/flights/reservation/${reservationId}/travellers`
  return request.put<App.ApiResponse<UpdateFlightTravellers>, UpdateFlightTravellers>(url, { passengers }, { credentials: 'include' })
    .then((res) => res.result.passengers)
}

interface UpdateFlightReservation {
  contactInfo: {
    email: string
    phone: string
  }
  emergencyContact?: App.FlightEmergencyContact
}

interface UpdateFlightReservationDetails {
  contact_info: {
    email: string
    phone: string
  }
  emergency_contact?: {
    country_code: string
    first_name: string
    last_name: string
    phone: string
    relationship: string
  }
}

export function updateFlightReservationDetails(reservationId: string, { contactInfo, emergencyContact }: UpdateFlightReservation) {
  const url = `/api/flights/reservation/${reservationId}/details`
  return request.put<App.ApiResponse<App.FlightOrderItemDetails>, UpdateFlightReservationDetails>(url, {
    contact_info: contactInfo,
    emergency_contact: emergencyContact ? {
      country_code: emergencyContact.countryCode,
      first_name: emergencyContact.firstName,
      last_name: emergencyContact.lastName,
      phone: emergencyContact.phone,
      relationship: emergencyContact.relationship,
    } : undefined,
  }, {
    credentials: 'include'
  })
    .then(res => {
      return res.result
    })
}

export function getRecentFlightSearches(): Promise<Array<App.RecentFlightSearch>> {
  const url = '/api/flights/flightSearch/recent'

  return request.get<App.ApiResponse<Array<App.RecentFlightSearch>>>(url, {
    credentials: 'include',
  }).then(res => res.result)
}

export function lockFlightReservation(reservationId: string) {
  return request.patch<App.ApiResponse, { op: string }>(
    `/api/flights/reservation/${reservationId}`,
    { op: 'lock' },
    { credentials: 'include' }
  ).then((res) => {
    return res.result
  })
}

export interface MappedDateChangeFlightSearchResult extends MappedFlightSearchResult {
  fareIdsToUpsellFarePerLeg: Record<string, App.FlightDateChangeUpsellFaresResult>
}

export function getFlightsDateChangeFlightsList(reservationId: string, region: string, selectedLegs: Array<App.LegWithFlightItemIdWithNewDepartureDate>): Promise<MappedDateChangeFlightSearchResult> {
  const url = `/api/flights/reservation/${reservationId}/addons/exchange-dates/search`

  return request.post<App.ApiResponse<ServerExchangeDateFlightSearchResult>, any>(url, {
    region,
    selectedLegs: selectedLegs.map(leg => ({
      legId: leg.id,
      // TODO: update once calendar is implemented
      newDepartureDate: leg.newDepartureDate,
    })),
  }, { credentials: 'include' })
    .then<MappedDateChangeFlightSearchResult>(res => {
      const { onewayFares, fareFamilies, searchId } = res.result

      return {
        onewayFares: (onewayFares ?? []).map((oneWayFare) => {
          return oneWayFare.fares.filter(fare => fare !== null).map((fare) => journeyV2Map(fare, fareFamilies))
        }),
        fareFamilies,
        searchId,
        timestamp: Date.now(),
        fareIdsToUpsellFarePerLeg: Object.keys(res.result.fareIdsToUpsellFarePerLeg).reduce((acc, legId) => {
          const upsellFares = res.result.fareIdsToUpsellFarePerLeg[legId]
          const upsellPerLeg = (upsellFares.upsellPerLeg ?? []).map(leg => leg.upsellFares.map(mapFlightFareFamily))

          acc[legId] = { upsellPerLeg, isMixedUpsell: upsellFares.isMixedUpsell }
          return acc
        }, {} as Record<string, App.FlightDateChangeUpsellFaresResult>)
      }
    })
}

export interface FlightSeatTraveler {
  id: string;
  type: 'ADT' | 'CHD' | 'INF';
}

interface GetSeatsParams {
  searchId: string;
  currency: string;
  region: string;
  isStandalone: boolean;
  brand: string;
  selectedFareIds: Array<string>;
  travelers: Array<FlightSeatTraveler>;
}

export function getFlightSeats(params: GetSeatsParams): Promise<App.FlightsSeatsAvailability> {
  const { searchId, currency, region, isStandalone, brand, selectedFareIds, travelers } = params

  return request.post<App.ApiResponse<ServerSeatResponse>, GetSeatsParams>('/api/flights/addons/seats/availability', {
    searchId,
    currency,
    region,
    isStandalone,
    brand,
    selectedFareIds,
    travelers,
  }, { credentials: 'include' })
    .then(res => seatsAvailabilityMap(res.result))
}
